import { useAuth } from '@/contexts/AuthContext';
import { getWarehousesForUser, type WarehouseResponse } from '@/api/warehouse.api';
import type { WarehouseType } from '@/types/shared';
import { useRef, useState, useEffect, useCallback, useMemo } from 'react';
import ConfirmDialog from '@/components/feature/ConfirmDialog';
import { useWarehouseStore } from '@/stores/warehouseStore';
import { complianceApi } from '@/api/compliance.api';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface PartyFormData {
  name: string;
  type: 'customer' | 'supplier' | 'both';
  gstin: string;
  pan: string;
  phone: string;
  email: string;
  city: string;
  billingAddress: string;
  shippingAddress: string;
  stateCode: string;
  stateName: string;
  warehouseId: string;
  warehouseName: string;
  creditLimit: number;
  creditDays: number;
  openingBalance: number;
  isActive: boolean;
  // GSTIN verification result (Whitebooks GST Return Filing /public/search)
  gstinVerified?: boolean;
  gstinStatus?: string;
  gstinTradeName?: string;
  gstinLegalName?: string;
  gstinRegistrationDate?: string;
  gstinTaxpayerType?: string;
  gstinCancelledDate?: string;
  // TDS deduction profile — set when this party is required under the IT Act
  // to deduct TDS before paying, or has TDS deducted from their own payments.
  isTdsApplicable?: boolean;
  tdsSection?: string;
  tdsRate?: number;
  tanNumber?: string;
  tdsCertificateFrequency?: string;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const GSTIN_REGEX =
  /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
// Whitebooks' 36 official NIC e-Invoice sandbox test GSTINs (e.g. 29AAGCB1286Q000)
// don't follow the real checksum-shaped format (entity code / literal "Z" are
// placeholders), so they fail GSTIN_REGEX by design. Accept them explicitly for
// sandbox testing without weakening real-GSTIN validation for actual parties.
const WHITEBOOKS_SANDBOX_GSTIN_REGEX = /^[0-9]{2}AAGCB1286Q[0-9]{3}$/;
const isAcceptableGstin = (v: string) =>
  GSTIN_REGEX.test(v) || WHITEBOOKS_SANDBOX_GSTIN_REGEX.test(v);
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PAN_REGEX   = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
const TAN_REGEX   = /^[A-Z]{4}[0-9]{5}[A-Z]{1}$/;

// Rates mirror the company-rate column of public.tds_sections (the same table backing
// the TDS/TCS Settings page) — kept here as a fallback so the dropdown auto-fills
// correctly even before that API call resolves, and "Others" always stays available
// for a manual rate the seeded list doesn't cover.
const FALLBACK_TDS_SECTIONS: { section: string; description: string; rateCompany: number }[] = [
  { section: '194C',  description: 'Contractors & Sub-contractors', rateCompany: 2 },
  { section: '194H',  description: 'Commission or Brokerage', rateCompany: 5 },
  { section: '194I',  description: 'Rent — Plant/Machinery', rateCompany: 2 },
  { section: '194IA', description: 'Rent — Land/Building (Transfer of Immovable Property)', rateCompany: 10 },
  { section: '194J',  description: 'Professional/Technical Services', rateCompany: 10 },
  { section: '194Q',  description: 'Purchase of Goods above ₹50L', rateCompany: 0.1 },
];

const CERTIFICATE_FREQUENCY_OPTIONS = [
  { value: 'QUARTERLY', label: 'Quarterly (Q1/Q2/Q3/Q4) — most common' },
  { value: 'ANNUAL', label: 'Annual' },
];

const INDIAN_STATES = [
  { code: '01', name: 'Jammu & Kashmir' },
  { code: '02', name: 'Himachal Pradesh' },
  { code: '03', name: 'Punjab' },
  { code: '04', name: 'Chandigarh' },
  { code: '05', name: 'Uttarakhand' },
  { code: '06', name: 'Haryana' },
  { code: '07', name: 'Delhi' },
  { code: '08', name: 'Rajasthan' },
  { code: '09', name: 'Uttar Pradesh' },
  { code: '10', name: 'Bihar' },
  { code: '11', name: 'Sikkim' },
  { code: '12', name: 'Arunachal Pradesh' },
  { code: '13', name: 'Nagaland' },
  { code: '14', name: 'Manipur' },
  { code: '15', name: 'Mizoram' },
  { code: '16', name: 'Tripura' },
  { code: '17', name: 'Meghalaya' },
  { code: '18', name: 'Assam' },
  { code: '19', name: 'West Bengal' },
  { code: '20', name: 'Jharkhand' },
  { code: '21', name: 'Odisha' },
  { code: '22', name: 'Chhattisgarh' },
  { code: '23', name: 'Madhya Pradesh' },
  { code: '24', name: 'Gujarat' },
  { code: '25', name: 'Daman & Diu' },
  { code: '26', name: 'Dadra & Nagar Haveli' },
  { code: '27', name: 'Maharashtra' },
  { code: '28', name: 'Andhra Pradesh (Old)' },
  { code: '29', name: 'Karnataka' },
  { code: '30', name: 'Goa' },
  { code: '31', name: 'Lakshadweep' },
  { code: '32', name: 'Kerala' },
  { code: '33', name: 'Tamil Nadu' },
  { code: '34', name: 'Puducherry' },
  { code: '35', name: 'Andaman & Nicobar Islands' },
  { code: '36', name: 'Telangana' },
  { code: '37', name: 'Andhra Pradesh' },
  { code: '38', name: 'Ladakh' },
];

const STATE_CODE_MAP: Record<string, string> = Object.fromEntries(
  INDIAN_STATES.map((s) => [s.code, s.name]),
);

const TYPE_BADGE: Record<WarehouseType, { label: string; cls: string; icon: string }> = {
  OFFICE:  { label: 'Office',  cls: 'bg-sky-100    text-sky-700',    icon: 'ri-building-line'   },
  FACTORY: { label: 'Factory', cls: 'bg-amber-100  text-amber-700',  icon: 'ri-building-4-line' },
  STORE:   { label: 'Store',   cls: 'bg-green-100  text-green-700',  icon: 'ri-store-2-line'    },
  GODOWN:  { label: 'Godown',  cls: 'bg-violet-100 text-violet-700', icon: 'ri-store-3-line'    },
  BRANCH:  { label: 'Branch',  cls: 'bg-indigo-100 text-indigo-700', icon: 'ri-map-pin-line'    },
  TRANSIT: { label: 'Transit', cls: 'bg-slate-100  text-slate-600',  icon: 'ri-truck-line'      },
};

const defaultForm: PartyFormData = {
  name: '', type: 'customer', gstin: '', pan: '', phone: '', email: '',
  city: '', billingAddress: '', shippingAddress: '', stateCode: '', stateName: '',
  warehouseId: '', warehouseName: '',
  creditLimit: 0, creditDays: 30, openingBalance: 0, isActive: true,
};

// ─── Props ────────────────────────────────────────────────────────────────────

interface PartyFormProps {
  open: boolean;
  initialData?: Partial<PartyFormData> & { id?: string };
  isEditing: boolean;
  onClose: () => void;
  onSave: (data: PartyFormData) => Promise<void>;
}

// ─── StateDropdown ────────────────────────────────────────────────────────────

interface StateDropdownProps {
  stateCode: string;
  stateName: string;
  onChange: (code: string, name: string) => void;
  navIndex?: number;
}

// function StateDropdown({ stateCode, stateName, onChange, navIndex }: StateDropdownProps) {
//   const [query, setQuery] = useState(stateName);
//   const [open, setOpen]   = useState(false);
//   const containerRef      = useRef<HTMLDivElement>(null);

//   // Keep query in sync when stateName changes externally (e.g. GSTIN auto-fill)
//   useEffect(() => { setQuery(stateName); }, [stateName]);

//   const filtered = useMemo(
//     () =>
//       INDIAN_STATES.filter(
//         (s) =>
//           s.name.toLowerCase().includes(query.toLowerCase()) ||
//           s.code.includes(query),
//       ),
//     [query],
//   );

//   useEffect(() => {
//     const handler = (e: MouseEvent) => {
//       if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
//         setOpen(false);
//         setQuery(stateName); // revert on outside click
//       }
//     };
//     document.addEventListener('mousedown', handler);
//     return () => document.removeEventListener('mousedown', handler);
//   }, [stateName]);

//   return (
//     <div ref={containerRef} className="relative">
//       <input
//         type="text"
//         value={query}
//         onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
//         onFocus={() => setOpen(true)}
//         placeholder="Search state..."
//         data-nav-index={navIndex}
//         className="w-full h-10 px-3 pr-8 rounded-lg border border-[#e2e8f0] text-sm text-[#1e293b] bg-white focus:outline-none focus:border-[#4f46e5] focus:ring-2 focus:ring-[#4f46e5]/20"
//       />
//       {stateCode && (
//         <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[10px] font-bold text-[#4f46e5] bg-indigo-50 px-1.5 py-0.5 rounded">
//           {stateCode}
//         </span>
//       )}
//       {open && filtered.length > 0 && (
//         <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border border-[#e2e8f0] rounded-lg shadow-lg max-h-48 overflow-y-auto">
//           {filtered.map((s) => (
//             <div
//               key={s.code}
//               onMouseDown={(e) => {
//                 e.preventDefault();
//                 onChange(s.code, s.name);
//                 setQuery(s.name);
//                 setOpen(false);
//               }}
//               className="flex items-center gap-2 px-3 py-2 hover:bg-[#f8fafc] cursor-pointer text-sm"
//             >
//               <span className="text-[10px] font-mono font-bold text-[#94a3b8] w-6">{s.code}</span>
//               <span className="text-[#1e293b]">{s.name}</span>
//             </div>
//           ))}
//         </div>
//       )}
//     </div>
//   );
// }

// ─── PartyForm ────────────────────────────────────────────────────────────────
// FIX: removed console.log('PartyFormmmmm', PartyForm) that was above the
// function definition — PartyForm didn't exist at that point (would throw ReferenceError).

function StateDropdown({ stateCode, stateName, onChange, navIndex }: StateDropdownProps) {
  const [query, setQuery]       = useState(stateName);
  const [open, setOpen]         = useState(false);
  const [highlighted, setHighlighted] = useState(-1);  // ← track keyboard cursor
  const containerRef            = useRef<HTMLDivElement>(null);
  const listRef                 = useRef<HTMLDivElement>(null);

  // Keep query in sync when stateName changes externally (e.g. GSTIN auto-fill)
  useEffect(() => { setQuery(stateName); }, [stateName]);

  // Reset highlight when filtered list changes
  const filtered = useMemo(() => {
    setHighlighted(-1);
    return INDIAN_STATES.filter(
      (s) =>
        s.name.toLowerCase().includes(query.toLowerCase()) ||
        s.code.includes(query),
    );
  }, [query]);

  // Close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
        setQuery(stateName); // revert on outside click
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [stateName]);

  // Scroll highlighted item into view
  useEffect(() => {
    if (highlighted < 0 || !listRef.current) return;
    const item = listRef.current.children[highlighted] as HTMLElement;
    item?.scrollIntoView({ block: 'nearest' });
  }, [highlighted]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!open && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
      setOpen(true);
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlighted((prev) => Math.min(prev + 1, filtered.length - 1));
        break;

      case 'ArrowUp':
        e.preventDefault();
        setHighlighted((prev) => Math.max(prev - 1, 0));
        break;

      case 'Enter':
        e.preventDefault();
        if (highlighted >= 0 && filtered[highlighted]) {
          const s = filtered[highlighted];
          onChange(s.code, s.name);
          setQuery(s.name);
          setOpen(false);
          setHighlighted(-1);
        }
        break;

      case 'Escape':
        setOpen(false);
        setQuery(stateName); // revert
        setHighlighted(-1);
        break;

      default:
        break;
    }
  };

  return (
    <div ref={containerRef} className="relative">
      <input
        type="text"
        value={query}
        onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onKeyDown={handleKeyDown}                        // ← added
        placeholder="Search state..."
        data-nav-index={navIndex}
        className="w-full h-10 px-3 pr-8 rounded-lg border border-[#e2e8f0] text-sm text-[#1e293b] bg-white focus:outline-none focus:border-[#4f46e5] focus:ring-2 focus:ring-[#4f46e5]/20"
      />
      {stateCode && (
        <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[10px] font-bold text-[#4f46e5] bg-indigo-50 px-1.5 py-0.5 rounded">
          {stateCode}
        </span>
      )}
      {open && filtered.length > 0 && (
        <div
          ref={listRef}                                  // ← added for scroll
          className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border border-[#e2e8f0] rounded-lg shadow-lg max-h-48 overflow-y-auto"
        >
          {filtered.map((s, idx) => (
            <div
              key={s.code}
              onMouseDown={(e) => {
                e.preventDefault();
                onChange(s.code, s.name);
                setQuery(s.name);
                setOpen(false);
                setHighlighted(-1);
              }}
              onMouseEnter={() => setHighlighted(idx)}  // ← sync mouse + keyboard
              className={[
                'flex items-center gap-2 px-3 py-2 cursor-pointer text-sm transition-colors',
                idx === highlighted
                  ? 'bg-indigo-50 text-[#4f46e5]'      // ← highlighted row
                  : 'hover:bg-[#f8fafc] text-[#1e293b]',
              ].join(' ')}
            >
              <span className="text-[10px] font-mono font-bold text-[#94a3b8] w-6">{s.code}</span>
              <span>{s.name}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
export default function PartyForm({
  open, initialData, isEditing, onClose, onSave,
}: PartyFormProps) {
  const formRef     = useRef<HTMLDivElement>(null);
  const whDropdownRef = useRef<HTMLDivElement>(null);

  const { selectedWarehouseId, selectedWarehouseName, setSelectedWarehouse } = useWarehouseStore();
  const { user: authUser, hasControl } = useAuth();

  const canViewAll   = hasControl('viewAllWarehouses');
  const isSuperAdmin = authUser?.role === 'SUPER_ADMIN';

  // ── State ──────────────────────────────────────────────────────────────────
  const [form, setForm]                   = useState<PartyFormData>(() => ({
    ...defaultForm,
    ...initialData,
    // FIX: safe optional chaining; edit uses item's own warehouse, add uses store
    warehouseId:   isEditing ? (initialData?.warehouseId   ?? '') : (selectedWarehouseId   ?? ''),
    warehouseName: isEditing ? (initialData?.warehouseName ?? '') : (selectedWarehouseName ?? ''),
  }));
  const [gstRegistered, setGstRegistered] = useState(!!initialData?.gstin);
  const [verifyingGstin, setVerifyingGstin] = useState(false);
  const [gstinVerifyError, setGstinVerifyError] = useState('');
  const [gstinNotFound, setGstinNotFound] = useState(false);
  const [tdsSections, setTdsSections] = useState<{ section: string; description: string; rateCompany: number }[]>(FALLBACK_TDS_SECTIONS);
  const [errors, setErrors]               = useState<Record<string, string>>({});
  const [apiError, setApiError]           = useState('');
  const [isSaving, setIsSaving]           = useState(false);
  const [isDirty, setIsDirty]             = useState(false);
  const [discardConfirm, setDiscardConfirm] = useState(false);
  const [sameAddress, setSameAddress]     = useState(false);
  const [balanceMode, setBalanceMode]     = useState<'RECEIVE' | 'PAY'>('RECEIVE');
  const [openingBalanceAbs, setOpeningBalanceAbs] = useState<number>(0);
  const [showWarehouseDropdown, setShowWarehouseDropdown] = useState(false);
  const [warehouseSearch, setWarehouseSearch] = useState('');
  const [rawWarehouses, setRawWarehouses] = useState<
    Array<{ id: string; name: string; type: WarehouseType; isPrimary: boolean; isActive: boolean }>
  >([]);
  const [warehousesLoading, setWarehousesLoading] = useState(false);

  // ── Fetch warehouses ───────────────────────────────────────────────────────
  useEffect(() => {
    let mounted = true;
    setWarehousesLoading(true);
    void (async () => {
      try {
        const res = await getWarehousesForUser(canViewAll);
        if (!mounted) return;
        setRawWarehouses(
          (res.data ?? []).map((w: WarehouseResponse) => ({
            id:        w.id,
            name:      w.name,
            type:      w.type as WarehouseType,
            isPrimary: (w as any).is_primary ?? false,
            isActive:  w.is_active,
          })),
        );
      } catch {
        if (mounted) setRawWarehouses([]);
      } finally {
        if (mounted) setWarehousesLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [canViewAll]);

  // ── Fetch TDS sections (reuses the same tds_sections table/endpoint the
  // TDS/TCS Settings page uses — no separate list maintained here) ────────────
  useEffect(() => {
    let mounted = true;
    void (async () => {
      try {
        const res = await complianceApi.getTDSSections();
        if (!mounted || !res?.success || !Array.isArray(res.data)) return;
        const rows = (res.data as Array<{ section: string; description: string; rateCompany: number; isActive?: boolean }>)
          .filter((s) => s.isActive !== false && s.section !== '206C'); // 206C is TCS, not TDS
        if (rows.length > 0) {
          setTdsSections(rows.map((s) => ({ section: s.section, description: s.description, rateCompany: Number(s.rateCompany) })));
        }
      } catch {
        // keep FALLBACK_TDS_SECTIONS
      }
    })();
    return () => { mounted = false; };
  }, []);

  // FIX: memoize so reference is stable, avoiding re-render loops
  const accessibleWarehouses = useMemo(
    () => rawWarehouses.filter((w) => w.isActive),
    [rawWarehouses],
  );

  const filteredWarehouses = useMemo(
    () =>
      accessibleWarehouses.filter((wh) =>
        wh.name.toLowerCase().includes(warehouseSearch.toLowerCase()),
      ),
    [accessibleWarehouses, warehouseSearch],
  );

  // ── Reset on open ──────────────────────────────────────────────────────────
  // FIX: ONE useEffect only — the original had two conflicting effects both
  // reacting to [open, initialData]. The second one always ran after and wiped
  // the warehouseId logic from the first. Merged into a single effect.
  useEffect(() => {
    if (!open) return;

    const warehouseId   = isEditing
      ? (initialData?.warehouseId   ?? '')
      : (selectedWarehouseId        ?? '');
    const warehouseName = isEditing
      ? (initialData?.warehouseName ?? '')
      : (selectedWarehouseName      ?? '');

    setForm({ ...defaultForm, ...initialData, warehouseId, warehouseName });
    const ob = Number(initialData?.openingBalance ?? 0);
    const initialMode = ob < 0 ? 'PAY' : 'RECEIVE';
    const initialAbs = Math.abs(ob);
    setBalanceMode(initialMode);
    setOpeningBalanceAbs(initialAbs);
    setGstRegistered(!!initialData?.gstin);
    setErrors({});
    setApiError('');
    setIsDirty(false);
    // FIX: sameAddress initialized from actual data rather than always false
    setSameAddress(
      !!(initialData?.billingAddress &&
        initialData.billingAddress === initialData?.shippingAddress),
    );
  }, [open, isEditing]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Outside click closes warehouse dropdown ────────────────────────────────
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (!whDropdownRef.current?.contains(e.target as Node)) {
        setShowWarehouseDropdown(false);
        setWarehouseSearch('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // ── Derived ────────────────────────────────────────────────────────────────
  const selectedWarehouse = accessibleWarehouses.find((w) => w.id === form.warehouseId);
  const selectedBadge     = selectedWarehouse ? (TYPE_BADGE[selectedWarehouse.type] ?? null) : null;

  // ── Helpers ────────────────────────────────────────────────────────────────
  const update = useCallback(
    (field: keyof PartyFormData, value: string | number | boolean | undefined) => {
      setForm((p) => ({ ...p, [field]: value }));
      setIsDirty(true);
      setApiError('');
      setErrors((e) => {
        if (!e[field]) return e;
        const n = { ...e }; delete n[field]; return n;
      });
    },
    [],
  );

  const handleWarehouseSelect = useCallback(
    (id: string, name: string) => {
      setForm((p) => ({ ...p, warehouseId: id, warehouseName: name }));
      setSelectedWarehouse(id, name); // FIX: also sync to global store
      setIsDirty(true);
      setShowWarehouseDropdown(false);
      setWarehouseSearch('');
    },
    [setSelectedWarehouse],
  );

  const handleGstinChange = (val: string) => {
    const upper = val.toUpperCase();
    update('gstin', upper);
    if (upper.length >= 2) {
      const code = upper.slice(0, 2);
      const name = STATE_CODE_MAP[code] ?? '';
      update('stateCode', code);
      update('stateName', name);
    }
    // Editing the GSTIN invalidates any prior verification result for a different number.
    if (form.gstinVerified !== undefined) {
      update('gstinVerified', undefined);
      update('gstinStatus', '');
      update('gstinCancelledDate', '');
    }
    setGstinVerifyError('');
    setGstinNotFound(false);
  };

  const handleVerifyGstin = async () => {
    const gstin = form.gstin.trim().toUpperCase();
    if (!isAcceptableGstin(gstin)) {
      setGstinVerifyError('Enter a valid GSTIN format first');
      return;
    }
    setVerifyingGstin(true);
    setGstinVerifyError('');
    setGstinNotFound(false);
    try {
      const res = await complianceApi.verifyTdsGstin(gstin, initialData?.id);
      if (res?.success && res.data) {
        // RESULT 1 (Active) / RESULT 2 (Cancelled) — either way GSTN answered, so
        // record exactly what it said rather than collapsing both into one state.
        update('gstinVerified', res.data.isActive);
        update('gstinStatus', res.data.status || '');
        update('gstinTradeName', res.data.tradeName || '');
        update('gstinLegalName', res.data.legalName || '');
        update('gstinRegistrationDate', res.data.registrationDate || '');
        update('gstinTaxpayerType', res.data.taxpayerType || '');
        update('gstinCancelledDate', res.data.cancelledDate || '');
        if (!form.name.trim() && (res.data.tradeName || res.data.legalName)) {
          update('name', res.data.tradeName || res.data.legalName || '');
        }
      } else if (res?.notFound) {
        // RESULT 3 — Not Found: allow save, but flag as unverified rather than
        // blocking (the backend already flips the party to unverified on this path).
        update('gstinVerified', false);
        update('gstinStatus', 'NOT_FOUND');
        setGstinNotFound(true);
      } else {
        setGstinVerifyError(res?.message || 'GSTIN verification failed');
      }
    } catch (err: any) {
      setGstinVerifyError(err?.message || 'GSTIN verification failed');
    } finally {
      setVerifyingGstin(false);
    }
  };

  const handleTdsSectionChange = (section: string) => {
    update('tdsSection', section);
    if (section === 'OTHERS') return; // manual rate entry
    const match = tdsSections.find((s) => s.section === section);
    if (match) update('tdsRate', match.rateCompany);
  };

  const handleToggleGst = () => {
    const next = !gstRegistered;
    setGstRegistered(next);
    if (!next) update('gstin', '');
    setIsDirty(true);
  };

  const handleClose = useCallback(() => {
    if (isDirty) { setDiscardConfirm(true); }
    else { onClose(); }
  }, [isDirty, onClose]);

  // Esc key
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.preventDefault(); handleClose(); }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, handleClose]);

  // ── Validation ─────────────────────────────────────────────────────────────
  const validate = () => {
    const e: Record<string, string> = {};
    if (!form.name.trim())
      e.name = 'Party name is required';

    // ── Mandatory State Selection ──
    if (!form.stateCode || !form.stateCode.trim()) {
      e.stateCode = 'State selection is mandatory';
    }

    if (form.phone && !/^\d{10}$/.test(form.phone.trim()))
      e.phone = 'Mobile number must be exactly 10 digits';
    if (form.email && !EMAIL_REGEX.test(form.email.trim()))
      e.email = 'Enter a valid email address';

    // ── GSTIN & State Code Matching ──
    const rawGstin = form.gstin.trim().toUpperCase();
    if (gstRegistered && !rawGstin) {
      e.gstin = 'GSTIN is required when registered for GST';
    } else if (rawGstin) {
      if (!isAcceptableGstin(rawGstin)) {
        e.gstin = 'Invalid GSTIN format (15 chars, e.g. 27AADCR4849M1ZV)';
      }
      if (rawGstin.length >= 2) {
        const gstinStateCode = rawGstin.slice(0, 2);
        if (form.stateCode && gstinStateCode !== form.stateCode) {
          const gstinStateName = STATE_CODE_MAP[gstinStateCode] || `Code ${gstinStateCode}`;
          const selectedStateName = form.stateName || STATE_CODE_MAP[form.stateCode] || form.stateCode;
          e.gstin = `GSTIN state code (${gstinStateCode} - ${gstinStateName}) does not match selected state (${form.stateCode} - ${selectedStateName})`;
          e.stateCode = `Selected state (${form.stateCode}) does not match GSTIN prefix (${gstinStateCode})`;
        }
      }
    }

    if (form.pan && !PAN_REGEX.test(form.pan))
      e.pan = 'Invalid PAN format (e.g. AADCR4849M)';

    // ── TDS Configuration ──
    if (form.isTdsApplicable) {
      if (!form.tdsSection)
        e.tdsSection = 'Select a TDS section';
      if (form.tdsRate === undefined || form.tdsRate === null || Number.isNaN(Number(form.tdsRate)) || Number(form.tdsRate) < 0)
        e.tdsRate = 'Enter a valid TDS rate';
    }
    if (form.tanNumber && !TAN_REGEX.test(form.tanNumber.trim().toUpperCase()))
      e.tanNumber = 'Invalid TAN format (e.g. MUMU12345A)';

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setIsSaving(true);
    setApiError('');
    try {
      const payload = { ...form };
      if (!gstRegistered) payload.gstin = '';
      await onSave(payload);
      setIsDirty(false);
    } catch (err) {
      setApiError(err instanceof Error ? err.message : 'Failed to save party');
    } finally {
      setIsSaving(false);
    }
  };

  if (!open) return null;

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <>
      <div className="fixed inset-0 z-50 flex">
        {/* Overlay */}
        <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={handleClose} />

        {/* Panel */}
        <div ref={formRef} className="relative ml-auto h-full w-full max-w-lg bg-white shadow-2xl flex flex-col">

          {/* ── Header ── */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-[#e2e8f0] shrink-0">
            <div>
              <h3 className="text-base font-semibold text-[#1e293b]">
                {isEditing ? 'Edit Party' : 'Add New Party'}
              </h3>
              <p className="text-xs text-[#64748b] mt-0.5">Customer, Supplier or Both</p>
            </div>
            <button
              onClick={handleClose}
              className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-[#f1f5f9] text-[#64748b] cursor-pointer transition-colors"
            >
              <i className="ri-close-line text-lg" />
            </button>
          </div>

          {/* ── API error banner ── */}
          {apiError && (
            <div className="mx-6 mt-4 flex items-start gap-2 p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm shrink-0">
              <i className="ri-error-warning-line text-base shrink-0 mt-0.5" />
              <p>{apiError}</p>
            </div>
          )}

          {/* ── Form body ── */}
          <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">

            {/* ── WAREHOUSE ── */}
            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-[#64748b] uppercase tracking-wide flex items-center gap-2">
                Warehouse
                {warehousesLoading && <i className="ri-loader-4-line animate-spin text-[#94a3b8]" />}
              </label>

              <div className="relative" ref={whDropdownRef}>
                {/* Trigger */}
                <button
                  type="button"
                  onClick={() => setShowWarehouseDropdown((p) => !p)}
                  disabled={warehousesLoading}
                  className="w-full flex items-center gap-2 h-11 px-3 rounded-xl border border-[#e2e8f0] text-sm bg-white hover:border-[#4f46e5] hover:bg-indigo-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                >
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${selectedBadge?.cls ?? 'bg-slate-100'}`}>
                    <i className={`${selectedBadge?.icon ?? 'ri-store-3-line'} text-sm ${!selectedBadge ? 'text-[#64748b]' : ''}`} />
                  </div>
                  <div className="flex-1 text-left min-w-0">
                    <p className="text-sm font-medium truncate text-[#1e293b]">
                      {selectedWarehouse?.name ?? 'Select Warehouse'}
                    </p>
                    {selectedBadge && (
                      <p className="text-[10px] text-[#94a3b8]">{selectedBadge.label}</p>
                    )}
                  </div>
                  <i className={`ri-arrow-down-s-line text-[#64748b] text-lg transition-transform duration-200 ${showWarehouseDropdown ? 'rotate-180' : ''}`} />
                </button>

                {/* Dropdown */}
                {showWarehouseDropdown && (
                  <div className="absolute left-0 top-12 w-full bg-white border border-[#e2e8f0] rounded-2xl shadow-xl z-50 overflow-hidden">
                    {/* Search */}
                    <div className="p-3 border-b border-[#f1f5f9]">
                      <div className="relative">
                        <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-[#94a3b8]" />
                        <input
                          type="text"
                          value={warehouseSearch}
                          onChange={(e) => setWarehouseSearch(e.target.value)}
                          placeholder="Search warehouse..."
                          className="w-full h-10 pl-10 pr-3 rounded-lg border border-[#e2e8f0] text-sm focus:outline-none focus:border-[#4f46e5]"
                        />
                      </div>
                    </div>

                    <div className="px-3 py-2 border-b border-[#f1f5f9]">
                      <p className="text-[10px] font-bold text-[#94a3b8] uppercase tracking-wide">
                        {isSuperAdmin ? 'All Warehouses' : 'Your Assigned Warehouses'}
                      </p>
                    </div>

                    <div className="max-h-64 overflow-y-auto">
                      {filteredWarehouses.length === 0 ? (
                        <div className="p-6 text-center text-sm text-[#94a3b8]">
                          No warehouse found
                        </div>
                      ) : (
                        filteredWarehouses.map((wh) => {
                          const badge      = TYPE_BADGE[wh.type] ?? null;
                          const isSelected = wh.id === form.warehouseId;
                          return (
                            <button
                              type="button"
                              key={wh.id}
                              onClick={() => handleWarehouseSelect(wh.id, wh.name)}
                              className={`w-full text-left px-3 py-3 flex items-center gap-3 hover:bg-[#f8fafc] transition-colors cursor-pointer ${
                                isSelected ? 'bg-indigo-50' : ''
                              }`}
                            >
                              <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${badge?.cls ?? 'bg-slate-100'}`}>
                                <i className={`${badge?.icon ?? 'ri-store-3-line'} text-sm`} />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className={`text-sm font-medium truncate ${isSelected ? 'text-[#4f46e5]' : 'text-[#1e293b]'}`}>
                                  {wh.name}
                                </p>
                                <div className="flex items-center gap-2 mt-0.5">
                                  <span className="text-[10px] text-[#94a3b8]">{badge?.label ?? wh.type}</span>
                                  {wh.isPrimary && (
                                    <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 font-semibold">
                                      Primary
                                    </span>
                                  )}
                                </div>
                              </div>
                              {isSelected && <i className="ri-check-line text-[#4f46e5] text-lg shrink-0" />}
                            </button>
                          );
                        })
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* ── Party Name ── */}
            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-[#64748b] uppercase tracking-wide">
                Party Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => update('name', e.target.value)}
                placeholder="Ramesh Electronics"
                data-nav-index={0}
                className={`w-full h-10 px-3 rounded-lg border text-sm text-[#1e293b] bg-white focus:outline-none focus:ring-2 transition-colors ${
                  errors.name
                    ? 'border-red-400 focus:ring-red-200'
                    : 'border-[#e2e8f0] focus:border-[#4f46e5] focus:ring-[#4f46e5]/20'
                }`}
              />
              {errors.name && <p className="text-xs text-red-500">{errors.name}</p>}
            </div>

            {/* ── Party Type ── */}
            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-[#64748b] uppercase tracking-wide">Party Type</label>
              <div className="flex gap-2">
                {(['customer', 'supplier', 'both'] as const).map((t) => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => update('type', t)}
                    className={`flex-1 h-9 rounded-lg text-xs font-semibold border transition-colors cursor-pointer capitalize ${
                      form.type === t
                        ? 'bg-[#4f46e5] text-white border-[#4f46e5]'
                        : 'bg-white text-[#64748b] border-[#e2e8f0] hover:border-[#4f46e5]'
                    }`}
                  >
                    {t.charAt(0).toUpperCase() + t.slice(1)}
                  </button>
                ))}
              </div>
            </div>

            {/* ── Mobile + Email ── */}
            <div className="grid grid-cols-2 gap-3">
              {/* Mobile */}
              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-[#64748b] uppercase tracking-wide">Mobile</label>
                <div className="relative">
                  <input
                    type="tel"
                    value={form.phone}
                    onChange={(e) => update('phone', e.target.value.replace(/\D/g, '').slice(0, 10))}
                    placeholder="9812345670"
                    inputMode="numeric"
                    maxLength={10}
                    data-nav-index={2}
                    className={`w-full h-10 px-3 pr-14 rounded-lg border text-sm text-[#1e293b] bg-white focus:outline-none focus:ring-2 transition-colors ${
                      form.phone.length === 10
                        ? 'border-green-400 focus:ring-green-200'
                        : form.phone.length > 0
                          ? 'border-red-400 focus:ring-red-200'
                          : 'border-[#e2e8f0] focus:border-[#4f46e5] focus:ring-[#4f46e5]/20'
                    }`}
                  />
                  <span className={`absolute right-3 top-1/2 -translate-y-1/2 text-xs font-medium ${
                    form.phone.length === 10 ? 'text-green-500'
                    : form.phone.length > 0   ? 'text-amber-500'
                    : 'text-[#94a3b8]'
                  }`}>
                    {form.phone.length}/10
                  </span>
                </div>
                {form.phone.length > 0 && form.phone.length < 10 ? (
                  <p className="text-xs text-red-500 flex items-center gap-1">
                    <i className="ri-error-warning-line" /> Must be 10 digits
                  </p>
                ) : form.phone.length === 10 ? (
                  <p className="text-xs text-green-500 flex items-center gap-1">
                    <i className="ri-checkbox-circle-line" /> Valid mobile number
                  </p>
                ) : null}
              </div>

              {/* Email */}
              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-[#64748b] uppercase tracking-wide">Email</label>
                <div className="relative">
                  <input
                    type="email"
                    value={form.email}
                    onChange={(e) => update('email', e.target.value)}
                    placeholder="party@company.in"
                    data-nav-index={3}
                    className={`w-full h-10 px-3 pr-8 rounded-lg border text-sm text-[#1e293b] bg-white focus:outline-none focus:ring-2 transition-colors ${
                      form.email.length > 0 && !EMAIL_REGEX.test(form.email)
                        ? 'border-red-400 focus:ring-red-200'
                        : form.email.length > 0 && EMAIL_REGEX.test(form.email)
                          ? 'border-green-400 focus:ring-green-200'
                          : 'border-[#e2e8f0] focus:border-[#4f46e5] focus:ring-[#4f46e5]/20'
                    }`}
                  />
                  {form.email.length > 0 && (
                    <span className="absolute right-3 top-1/2 -translate-y-1/2">
                      {EMAIL_REGEX.test(form.email)
                        ? <i className="ri-checkbox-circle-line text-green-500 text-sm" />
                        : <i className="ri-error-warning-line text-red-400 text-sm" />}
                    </span>
                  )}
                </div>
                {form.email.length > 0 && !EMAIL_REGEX.test(form.email) ? (
                  <p className="text-xs text-red-500 flex items-center gap-1">
                    <i className="ri-error-warning-line" /> Enter a valid email
                  </p>
                ) : form.email.length > 0 && EMAIL_REGEX.test(form.email) ? (
                  <p className="text-xs text-green-500 flex items-center gap-1">
                    <i className="ri-checkbox-circle-line" /> Valid email
                  </p>
                ) : null}
              </div>
            </div>

            {/* ── GST Toggle ── */}
            <div className={`flex items-center gap-3 p-3 rounded-lg border transition-all duration-300 ${
              gstRegistered ? 'bg-indigo-50 border-indigo-200' : 'bg-[#f8fafc] border-[#e2e8f0]'
            }`}>
              <button
                type="button"
                onClick={handleToggleGst}
                data-nav-index={4}
                className={`relative w-11 h-6 rounded-full transition-all duration-300 cursor-pointer shrink-0 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-[#4f46e5]/40 ${
                  gstRegistered ? 'bg-[#4f46e5]' : 'bg-[#e2e8f0]'
                }`}
              >
                <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow-sm transition-transform duration-300 flex items-center justify-center ${
                  gstRegistered ? 'translate-x-5' : 'translate-x-0'
                }`}>
                  <i className={`text-[8px] ${gstRegistered ? 'ri-check-line text-[#4f46e5]' : 'ri-close-line text-[#94a3b8]'}`} />
                </span>
              </button>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-medium text-[#1e293b]">Registered under GST</p>
                  <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${
                    gstRegistered ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'
                  }`}>
                    {gstRegistered ? 'Registered' : 'Unregistered'}
                  </span>
                </div>
                <p className="text-xs text-[#94a3b8] mt-0.5">
                  {gstRegistered ? 'GSTIN required for this party' : 'Unregistered — GSTIN not required'}
                </p>
              </div>
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 transition-all ${
                gstRegistered ? 'bg-indigo-100' : 'bg-slate-100'
              }`}>
                <i className={`ri-bill-line text-sm ${gstRegistered ? 'text-[#4f46e5]' : 'text-[#94a3b8]'}`} />
              </div>
            </div>

            {/* ── GSTIN ── */}
            {gstRegistered && (
              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-[#64748b] uppercase tracking-wide">GSTIN</label>
                <div className="flex items-center gap-2">
                  <div className="relative flex-1">
                    <input
                      type="text"
                      value={form.gstin}
                      onChange={(e) => handleGstinChange(e.target.value)}
                      placeholder="27AADCR4849M1ZV"
                      maxLength={15}
                      data-nav-index={5}
                      className={`w-full h-10 px-3 pr-9 rounded-lg border text-sm font-mono text-[#1e293b] bg-white focus:outline-none focus:ring-2 transition-colors uppercase ${
                        errors.gstin
                          ? 'border-red-400 focus:ring-red-200'
                          : form.gstin && GSTIN_REGEX.test(form.gstin)
                            ? 'border-green-400 focus:ring-green-200'
                            : 'border-[#e2e8f0] focus:border-[#4f46e5] focus:ring-[#4f46e5]/20'
                      }`}
                    />
                    {form.gstin && GSTIN_REGEX.test(form.gstin) && (
                      <i className="ri-checkbox-circle-fill absolute right-3 top-1/2 -translate-y-1/2 text-green-500 text-base" />
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={handleVerifyGstin}
                    disabled={verifyingGstin || !isAcceptableGstin(form.gstin.trim().toUpperCase())}
                    className="h-10 px-3 rounded-lg border border-[#4f46e5] text-xs font-semibold text-[#4f46e5] hover:bg-indigo-50 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap shrink-0 flex items-center gap-1.5"
                  >
                    {verifyingGstin ? <i className="ri-loader-4-line animate-spin" /> : <i className="ri-search-eye-line" />}
                    Verify
                  </button>
                </div>
                {errors.gstin && <p className="text-xs text-red-500">{errors.gstin}</p>}
                {gstinVerifyError && <p className="text-xs text-red-500">{gstinVerifyError}</p>}

                {/* RESULT 1 — Active */}
                {form.gstinVerified === true && (
                  <div className="text-xs rounded-lg px-3 py-2 space-y-1 bg-emerald-50 text-emerald-700 border border-emerald-200">
                    <div className="flex items-center gap-1.5 font-semibold">
                      <i className="ri-checkbox-circle-fill" /> GSTIN Verified (GSTN)
                    </div>
                    <div className="text-emerald-800 space-y-0.5">
                      {form.gstinTradeName && <p>Trade Name: {form.gstinTradeName}</p>}
                      {form.stateCode && <p>State Code: {form.stateCode}{form.stateName ? ` (${form.stateName})` : ''}</p>}
                      {form.gstinRegistrationDate && <p>Registered Since: {form.gstinRegistrationDate}</p>}
                      {form.gstinTaxpayerType && <p>Taxpayer Type: {form.gstinTaxpayerType}</p>}
                    </div>
                  </div>
                )}

                {/* RESULT 3 — Not Found: save is still allowed, flagged unverified */}
                {form.gstinVerified === false && gstinNotFound && (
                  <div className="text-xs rounded-lg px-3 py-2 space-y-1 bg-amber-50 text-amber-700 border border-amber-200">
                    <div className="flex items-center gap-1.5 font-semibold">
                      <i className="ri-alert-line" /> GSTIN not found on GSTN portal
                    </div>
                    <p className="text-amber-700">Verify the GSTIN number with the customer. It will be saved but flagged as unverified.</p>
                  </div>
                )}

                {/* RESULT 2 — Cancelled (or any other non-Active GSTN status) */}
                {form.gstinVerified === false && !gstinNotFound && (
                  <div className="text-xs rounded-lg px-3 py-2 space-y-1 bg-red-50 text-red-700 border border-red-200">
                    <div className="flex items-center gap-1.5 font-semibold">
                      <i className="ri-close-circle-fill" />
                      GSTIN {form.gstinStatus === 'Cancelled' ? 'Cancelled' : (form.gstinStatus || 'not Active')}
                      {form.gstinCancelledDate ? ` on ${form.gstinCancelledDate}` : ''}
                    </div>
                    <p className="text-red-700">Cannot do B2B transaction with a cancelled GSTIN — invoices to this customer will not carry valid ITC.</p>
                  </div>
                )}

                {form.gstinVerified === undefined && form.gstin && GSTIN_REGEX.test(form.gstin) && (
                  <p className="text-xs text-green-600 flex items-center gap-1">
                    <i className="ri-checkbox-circle-line" /> Valid GSTIN format — State auto-filled. Click Verify to confirm with GSTN.
                  </p>
                )}
              </div>
            )}

            {/* ── State ── */}
            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-[#64748b] uppercase tracking-wide">
                State <span className="text-red-500">*</span>
              </label>
              <StateDropdown
                stateCode={form.stateCode}
                stateName={form.stateName}
                onChange={(code, name) => { update('stateCode', code); update('stateName', name); }}
                navIndex={6}
              />
              {errors.stateCode && (
                <p className="text-xs text-red-500 flex items-center gap-1">
                  <i className="ri-error-warning-line" /> {errors.stateCode}
                </p>
              )}
            </div>

            {/* ── City ── */}
            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-[#64748b] uppercase tracking-wide">City</label>
              <input
                type="text"
                value={form.city}
                onChange={(e) => update('city', e.target.value)}
                placeholder="Pune"
                data-nav-index={7}
                className="w-full h-10 px-3 rounded-lg border border-[#e2e8f0] text-sm text-[#1e293b] bg-white focus:outline-none focus:border-[#4f46e5] focus:ring-2 focus:ring-[#4f46e5]/20"
              />
            </div>

            {/* ── Billing Address ── */}
            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-[#64748b] uppercase tracking-wide">Billing Address</label>
              <textarea
                value={form.billingAddress}
                onChange={(e) => { update('billingAddress', e.target.value); setSameAddress(false); }}
                rows={2}
                data-nav-index={8}
                placeholder="45, MG Road, Pune 411001"
                className="w-full px-3 py-2 rounded-lg border border-[#e2e8f0] text-sm text-[#1e293b] bg-white focus:outline-none focus:border-[#4f46e5] focus:ring-2 focus:ring-[#4f46e5]/20 resize-none"
              />
            </div>

            {/* ── Shipping Address ── */}
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <label className="text-xs font-semibold text-[#64748b] uppercase tracking-wide">Shipping Address</label>
                <button
                  type="button"
                  onClick={() => { update('shippingAddress', form.billingAddress); setSameAddress(true); }}
                  className="text-xs text-[#4f46e5] font-medium hover:text-indigo-700 cursor-pointer"
                >
                  Copy from Billing
                </button>
              </div>
              <textarea
                value={form.shippingAddress}
                onChange={(e) => { update('shippingAddress', e.target.value); setSameAddress(false); }}
                rows={2}
                placeholder="Same as billing or different"
                className="w-full px-3 py-2 rounded-lg border border-[#e2e8f0] text-sm text-[#1e293b] bg-white focus:outline-none focus:border-[#4f46e5] focus:ring-2 focus:ring-[#4f46e5]/20 resize-none"
              />
              {sameAddress && (
                <p className="text-xs text-[#94a3b8]">Copied from billing address</p>
              )}
            </div>

            {/* ── Credit + Opening Balance ── */}
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-[#64748b] uppercase tracking-wide">
                  Credit Limit (₹)
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  value={form.creditLimit || ''}
                  onChange={(e) => update('creditLimit', parseFloat(e.target.value) || 0)}
                  placeholder="600000"
                  data-nav-index={9}
                  className="w-full h-10 px-3 rounded-lg border border-[#e2e8f0] text-sm text-[#1e293b] bg-white focus:outline-none focus:border-[#4f46e5] focus:ring-2 focus:ring-[#4f46e5]/20"
                />
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-[#64748b] uppercase tracking-wide">
                  Credit Days
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  value={form.creditDays || ''}
                  onChange={(e) => update('creditDays', parseFloat(e.target.value) || 0)}
                  placeholder="30"
                  data-nav-index={10}
                  className="w-full h-10 px-3 rounded-lg border border-[#e2e8f0] text-sm text-[#1e293b] bg-white focus:outline-none focus:border-[#4f46e5] focus:ring-2 focus:ring-[#4f46e5]/20"
                />
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-[#64748b] uppercase tracking-wide">
                  Opening Balance
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  value={openingBalanceAbs || ''}
                  onChange={(e) => {
                    const val = parseFloat(e.target.value) || 0;
                    setOpeningBalanceAbs(val);
                    update('openingBalance', balanceMode === 'PAY' ? -Math.abs(val) : Math.abs(val));
                  }}
                  placeholder="0"
                  data-nav-index={11}
                  className="w-full h-10 px-3 rounded-lg border border-[#e2e8f0] text-sm text-[#1e293b] bg-white focus:outline-none focus:border-[#4f46e5] focus:ring-2 focus:ring-[#4f46e5]/20"
                />

                {/* To Receive / To Pay Radio Buttons */}
                <div className="flex items-center gap-4 pt-1">
                  <label className="inline-flex items-center gap-1.5 text-xs text-[#1e293b] font-medium cursor-pointer">
                    <input
                      type="radio"
                      name="openingBalanceMode"
                      value="RECEIVE"
                      checked={balanceMode === 'RECEIVE'}
                      onChange={() => {
                        setBalanceMode('RECEIVE');
                        update('openingBalance', Math.abs(openingBalanceAbs));
                      }}
                      className="w-4 h-4 text-[#4f46e5] border-[#e2e8f0] focus:ring-[#4f46e5] cursor-pointer"
                    />
                    <span>To Receive</span>
                  </label>

                  <label className="inline-flex items-center gap-1.5 text-xs text-[#1e293b] font-medium cursor-pointer">
                    <input
                      type="radio"
                      name="openingBalanceMode"
                      value="PAY"
                      checked={balanceMode === 'PAY'}
                      onChange={() => {
                        setBalanceMode('PAY');
                        update('openingBalance', -Math.abs(openingBalanceAbs));
                      }}
                      className="w-4 h-4 text-[#4f46e5] border-[#e2e8f0] focus:ring-[#4f46e5] cursor-pointer"
                    />
                    <span>To Pay</span>
                  </label>
                </div>
              </div>
            </div>

            {/* ── PAN ── */}
            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-[#64748b] uppercase tracking-wide">PAN</label>
              <div className="relative">
                <input
                  type="text"
                  value={form.pan}
                  onChange={(e) => update('pan', e.target.value.toUpperCase())}
                  placeholder="AADCR4849M"
                  maxLength={10}
                  className={`w-full h-10 px-3 pr-9 rounded-lg border text-sm font-mono text-[#1e293b] bg-white focus:outline-none focus:ring-2 transition-colors ${
                    errors.pan
                      ? 'border-red-400 focus:ring-red-200'
                      : form.pan.length === 10 && PAN_REGEX.test(form.pan)
                        ? 'border-green-400 focus:ring-green-200'
                        : 'border-[#e2e8f0] focus:border-[#4f46e5] focus:ring-[#4f46e5]/20'
                  }`}
                />
                {form.pan.length === 10 && PAN_REGEX.test(form.pan) && (
                  <i className="ri-checkbox-circle-fill absolute right-3 top-1/2 -translate-y-1/2 text-green-500 text-base" />
                )}
              </div>
              {errors.pan && <p className="text-xs text-red-500">{errors.pan}</p>}
            </div>

            {/* ── TDS CONFIGURATION ── */}
            <div className="pt-3 border-t border-[#e2e8f0] space-y-3">
              <p className="text-xs font-bold text-[#64748b] uppercase tracking-wide">TDS Configuration</p>

              <div className={`flex items-center gap-3 p-3 rounded-lg border transition-all duration-300 ${
                form.isTdsApplicable ? 'bg-indigo-50 border-indigo-200' : 'bg-[#f8fafc] border-[#e2e8f0]'
              }`}>
                <button
                  type="button"
                  onClick={() => update('isTdsApplicable', !form.isTdsApplicable)}
                  className={`relative w-11 h-6 rounded-full transition-all duration-300 cursor-pointer shrink-0 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-[#4f46e5]/40 ${
                    form.isTdsApplicable ? 'bg-[#4f46e5]' : 'bg-[#e2e8f0]'
                  }`}
                >
                  <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow-sm transition-transform duration-300 flex items-center justify-center ${
                    form.isTdsApplicable ? 'translate-x-5' : 'translate-x-0'
                  }`}>
                    <i className={`text-[8px] ${form.isTdsApplicable ? 'ri-check-line text-[#4f46e5]' : 'ri-close-line text-[#94a3b8]'}`} />
                  </span>
                </button>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium text-[#1e293b]">Is TDS Applicable?</p>
                    <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${
                      form.isTdsApplicable ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'
                    }`}>
                      {form.isTdsApplicable ? 'Enabled' : 'Off'}
                    </span>
                  </div>
                  <p className="text-xs text-[#94a3b8] mt-0.5">
                    Turn on if this party must deduct TDS before paying, or has TDS deducted from their payments
                  </p>
                </div>
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 transition-all ${
                  form.isTdsApplicable ? 'bg-indigo-100' : 'bg-slate-100'
                }`}>
                  <i className={`ri-percent-line text-sm ${form.isTdsApplicable ? 'text-[#4f46e5]' : 'text-[#94a3b8]'}`} />
                </div>
              </div>

              {form.isTdsApplicable && (
                <div className="space-y-3 pl-1">
                  {!form.pan.trim() && (
                    <div className="flex items-start gap-1.5 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                      <i className="ri-error-warning-line mt-0.5 shrink-0" />
                      <span>Without PAN, TDS rate will be doubled on all invoices (IT Act Section 206AA). Add a PAN above to avoid this.</span>
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <label className="text-xs font-semibold text-[#64748b] uppercase tracking-wide">
                        TDS Section <span className="text-red-500">*</span>
                      </label>
                      <select
                        value={form.tdsSection || ''}
                        onChange={(e) => handleTdsSectionChange(e.target.value)}
                        className={`w-full h-10 px-3 rounded-lg border text-sm text-[#1e293b] bg-white focus:outline-none focus:ring-2 transition-colors ${
                          errors.tdsSection ? 'border-red-400 focus:ring-red-200' : 'border-[#e2e8f0] focus:border-[#4f46e5] focus:ring-[#4f46e5]/20'
                        }`}
                      >
                        <option value="">Select section…</option>
                        {tdsSections.map((s) => (
                          <option key={s.section} value={s.section}>
                            {s.section} — {s.description} ({s.rateCompany}%)
                          </option>
                        ))}
                        <option value="OTHERS">Others — Manual rate entry</option>
                      </select>
                      {errors.tdsSection && <p className="text-xs text-red-500">{errors.tdsSection}</p>}
                    </div>

                    <div className="space-y-1.5">
                      <label className="text-xs font-semibold text-[#64748b] uppercase tracking-wide">
                        TDS Rate (%) <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        inputMode="decimal"
                        value={form.tdsRate ?? ''}
                        onChange={(e) => update('tdsRate', parseFloat(e.target.value) || 0)}
                        placeholder="2.00"
                        className={`w-full h-10 px-3 rounded-lg border text-sm text-[#1e293b] bg-white focus:outline-none focus:ring-2 transition-colors ${
                          errors.tdsRate ? 'border-red-400 focus:ring-red-200' : 'border-[#e2e8f0] focus:border-[#4f46e5] focus:ring-[#4f46e5]/20'
                        }`}
                      />
                      {errors.tdsRate && <p className="text-xs text-red-500">{errors.tdsRate}</p>}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <label className="text-xs font-semibold text-[#64748b] uppercase tracking-wide">Customer TAN Number</label>
                      <input
                        type="text"
                        value={form.tanNumber || ''}
                        onChange={(e) => update('tanNumber', e.target.value.toUpperCase())}
                        placeholder="MUMU12345A"
                        maxLength={10}
                        className={`w-full h-10 px-3 rounded-lg border text-sm font-mono text-[#1e293b] bg-white focus:outline-none focus:ring-2 transition-colors uppercase ${
                          errors.tanNumber ? 'border-red-400 focus:ring-red-200' : 'border-[#e2e8f0] focus:border-[#4f46e5] focus:ring-[#4f46e5]/20'
                        }`}
                      />
                      {errors.tanNumber ? (
                        <p className="text-xs text-red-500">{errors.tanNumber}</p>
                      ) : (
                        <p className="text-[11px] text-[#94a3b8]">Optional — used for GSTR7 reconciliation.</p>
                      )}
                    </div>

                    <div className="space-y-1.5">
                      <label className="text-xs font-semibold text-[#64748b] uppercase tracking-wide">When to Expect Form 16A</label>
                      <select
                        value={form.tdsCertificateFrequency || 'QUARTERLY'}
                        onChange={(e) => update('tdsCertificateFrequency', e.target.value)}
                        className="w-full h-10 px-3 rounded-lg border border-[#e2e8f0] text-sm text-[#1e293b] bg-white focus:outline-none focus:border-[#4f46e5] focus:ring-2 focus:ring-[#4f46e5]/20"
                      >
                        {CERTIFICATE_FREQUENCY_OPTIONS.map((o) => (
                          <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* ── Active toggle ── */}
            <div className="flex items-center gap-3 pt-1">
              {/* FIX: original had -translate-x-4/translate-x-1 (broken).
                  Correct: translate-x-0 → translate-x-4 matching left-1 origin */}
              <button
                type="button"
                onClick={() => update('isActive', !form.isActive)}
                className={`relative w-10 h-6 rounded-full transition-colors duration-200 cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#4f46e5]/30 ${
                  form.isActive ? 'bg-[#4f46e5]' : 'bg-[#e2e8f0]'
                }`}
              >
                <span className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full shadow transition-transform duration-200 ${
                  form.isActive ? 'translate-x-4' : 'translate-x-0'
                }`} />
              </button>
              <label className="text-sm text-[#64748b] select-none">
                {form.isActive ? 'Active Party' : 'Inactive Party'}
              </label>
            </div>
          </div>

          {/* ── Footer ── */}
          <div className="flex items-center justify-between gap-2 px-6 py-4 border-t border-[#e2e8f0] shrink-0">
            <div>
              {isDirty && (
                <span className="text-xs text-amber-600 flex items-center gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-500 inline-block" />
                  Unsaved changes
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={handleClose}
                className="h-9 px-4 rounded-lg border border-[#e2e8f0] text-sm font-medium text-[#64748b] hover:bg-[#f8fafc] transition-colors cursor-pointer whitespace-nowrap"
              >
                Cancel (Esc)
              </button>
              <button
                onClick={() => void handleSave()}
                disabled={isSaving}
                data-nav-index={12}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); void handleSave(); } }}
                className="flex items-center gap-2 h-9 px-4 rounded-lg bg-[#4f46e5] text-white text-sm font-semibold hover:bg-indigo-700 disabled:opacity-60 transition-colors cursor-pointer whitespace-nowrap focus:outline-none focus:ring-2 focus:ring-[#4f46e5]/40"
              >
                {isSaving
                  ? <><i className="ri-loader-4-line animate-spin" /> Saving...</>
                  : <><i className="ri-save-line" /> {isEditing ? 'Update Party' : 'Save Party'}</>
                }
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Discard confirm */}
      <ConfirmDialog
        open={discardConfirm}
        title="Discard changes?"
        message="You have unsaved changes. Are you sure you want to discard them?"
        variant="warning"
        confirmLabel="Yes, Discard (Y)"
        cancelLabel="No, Keep Editing (N)"
        onConfirm={() => { setDiscardConfirm(false); setIsDirty(false); onClose(); }}
        onCancel={() => setDiscardConfirm(false)}
      />
    </>
  );
}